import { Field, TextInput } from "./Field";

function maskSSN(v) {
    const d = (v || "").replace(/\D/g, "").slice(0, 9);
    if (d.length <= 3) return d;
    if (d.length <= 5) return `${d.slice(0, 3)}-${d.slice(3)}`;
    return `${d.slice(0, 3)}-${d.slice(3, 5)}-${d.slice(5)}`;
}
function maskPhone(v) {
    const d = (v || "").replace(/\D/g, "").slice(0, 10);
    if (d.length <= 3) return d;
    if (d.length <= 6) return `${d.slice(0, 3)}-${d.slice(3)}`;
    return `${d.slice(0, 3)}-${d.slice(3, 6)}-${d.slice(6)}`;
}
function maskDOB(v) {
    const d = (v || "").replace(/\D/g, "").slice(0, 8);
    if (d.length <= 2) return d;
    if (d.length <= 4) return `${d.slice(0, 2)}/${d.slice(2)}`;
    return `${d.slice(0, 2)}/${d.slice(2, 4)}/${d.slice(4)}`;
}

function dobToDate(s) {
    // Accept MM/DD/YYYY → Date
    const m = (s || "").match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
    if (!m) return null;
    const d = new Date(`${m[3]}-${m[1]}-${m[2]}T00:00:00Z`);
    return isNaN(d.getTime()) ? null : d;
}

export function validatePersonal(p) {
    const errs = {};
    if (!p?.first_name?.trim()) errs.first_name = "Required";
    if (!p?.last_name?.trim()) errs.last_name = "Required";
    const dob = dobToDate(p?.dob);
    if (!dob) errs.dob = "Use MM/DD/YYYY";
    else {
        const age = (Date.now() - dob.getTime()) / (365.25 * 24 * 3600 * 1000);
        if (age < 18 || age > 110) errs.dob = "Must be 18 or older";
    }
    if (!p?.street?.trim()) errs.street = "Required";
    if (!p?.zip || !/^\d{5}(-\d{4})?$/.test(p.zip)) errs.zip = "5-digit ZIP";
    if (!p?.city?.trim()) errs.city = "Required";
    if (!p?.state?.trim()) errs.state = "Required";
    const ssnDigits = (p?.ssn || "").replace(/\D/g, "");
    if (ssnDigits.length !== 9) errs.ssn = "9 digits required";
    const phoneDigits = (p?.mobile || "").replace(/\D/g, "");
    if (phoneDigits.length !== 10) errs.mobile = "10-digit US mobile";
    if (!p?.email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(p.email)) errs.email = "Valid email required";
    return errs;
}

export default function StepPersonal({ value, onChange, errors = {} }) {
    const v = value || {};
    const set = (k, val) => onChange({ ...v, [k]: val });

    return (
        <div className="space-y-7" data-testid="step-personal">
            <div>
                <div className="step-eyebrow">STEP 2</div>
                <h2 className="font-display text-3xl sm:text-4xl text-white mt-2">Personal Information</h2>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <Field label="First Name" error={errors.first_name} testId="field-first-name">
                    <TextInput value={v.first_name} onChange={(x) => set("first_name", x)} placeholder="First Name" testId="input-first-name" autoComplete="given-name" />
                </Field>
                <Field label="Last Name" error={errors.last_name} testId="field-last-name">
                    <TextInput value={v.last_name} onChange={(x) => set("last_name", x)} placeholder="Last Name" testId="input-last-name" autoComplete="family-name" />
                </Field>
                <Field label="Preferred Name" testId="field-preferred-name">
                    <TextInput value={v.preferred_name} onChange={(x) => set("preferred_name", x)} placeholder="Preferred Name" testId="input-preferred-name" />
                </Field>
                <Field label="Date Of Birth" error={errors.dob} testId="field-dob">
                    <TextInput
                        value={v.dob || ""}
                        onChange={(x) => set("dob", maskDOB(x))}
                        placeholder="MM/DD/YYYY"
                        testId="input-dob"
                        inputMode="numeric"
                        maxLength={10}
                    />
                </Field>

                <div className="sm:col-span-2">
                    <Field label="Street Address" error={errors.street} testId="field-street">
                        <TextInput value={v.street} onChange={(x) => set("street", x)} placeholder="Street Address" testId="input-street" autoComplete="street-address" />
                    </Field>
                </div>

                <Field label="Zip Code" error={errors.zip} testId="field-zip">
                    <TextInput
                        value={v.zip}
                        onChange={(x) => set("zip", x.replace(/[^\d-]/g, "").slice(0, 10))}
                        placeholder="12345"
                        testId="input-zip"
                        inputMode="numeric"
                    />
                </Field>
                <Field label="City" error={errors.city} testId="field-city">
                    <TextInput value={v.city} onChange={(x) => set("city", x)} placeholder="City" testId="input-city" />
                </Field>

                <Field label="State" error={errors.state} testId="field-state">
                    <TextInput
                        value={v.state || ""}
                        onChange={(x) => set("state", x.toUpperCase().slice(0, 2).replace(/[^A-Z]/g, ""))}
                        placeholder="State"
                        testId="input-state"
                        maxLength={2}
                    />
                </Field>
                <Field label="SSN" error={errors.ssn} testId="field-ssn">
                    <TextInput
                        value={v.ssn || ""}
                        onChange={(x) => set("ssn", maskSSN(x))}
                        placeholder="XXX-XX-XXXX"
                        testId="input-ssn"
                        inputMode="numeric"
                        maxLength={11}
                    />
                </Field>

                <div className="sm:col-span-2">
                    <Field label="Mobile" error={errors.mobile} testId="field-mobile">
                        <TextInput
                            value={v.mobile || ""}
                            onChange={(x) => set("mobile", maskPhone(x))}
                            placeholder="XXX-XXX-XXXX"
                            testId="input-mobile"
                            inputMode="tel"
                            maxLength={12}
                        />
                    </Field>
                </div>

                <div className="sm:col-span-2">
                    <Field label="Email" error={errors.email} testId="field-email">
                        <TextInput
                            type="email"
                            value={v.email}
                            onChange={(x) => set("email", x)}
                            placeholder="you@email.com"
                            testId="input-email"
                            autoComplete="email"
                        />
                    </Field>
                </div>
            </div>
        </div>
    );
}
